import { useContext, useEffect, useCallback } from "react";
import { useWatch, Controller } from "react-hook-form";
import { Groups } from "../../../../components/groups/Groups";
import { RecordContext } from "../../../../components/record/Record";
import { Box, FormControl, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { addMinutes } from "date-fns";
import pt from "date-fns/locale/pt";
import { useSearchParams } from "react-router-dom";
import { sessionsOptions } from "../../sessionsOptions";
import { RelatedInputField } from "../../../../components/fieldsInputs/fields/RelatedInputField/RelatedInputField";
import { GroupsTitle } from "../../../../components/groups/GroupsTitle";
import { useRecords } from "../../../../api/useRecords";
import { useServiceTimes } from "../../../../api/useServiceTimes";
import { useSessionTimeOptions } from "./useSessionTimeOptions";
import {
    CUSTOM_HANDLED_FIELDS,
    DEFAULT_TIME,
    toDate,
    formatDateTimeDisplay,
    combineDateAndTime,
    parseTimeString,
    parseStartDateParam,
    extractTimeFromDate,
    generateDefaultTimeOptions,
} from "./sessionFormUtils";
import { generateNoServiceTimeOptions } from "./temporaryNoServiceTimeOptions";

export const SessionFormContent = () => {
    const recordContext = useContext(RecordContext);
    const entityForm = recordContext?.entityForm;
    const item = recordContext?.item;
    const formatedFields = recordContext?.formatedFields;
    const [searchParams] = useSearchParams();

    const { data: servicesData } = useRecords("services", "id,name,max_duration", {
        staleTime: Infinity,
        extraOptions: { sort: "sort_order", filter: { sort_order: { _nnull: true } } },
    });
    const { data: serviceTimesData } = useServiceTimes();

    const watchedService = useWatch({ control: entityForm?.control, name: "service" });
    const watchedStartDate = useWatch({ control: entityForm?.control, name: "start_date" });
    const watchedEndDate = useWatch({ control: entityForm?.control, name: "end_date" });

    const currentService = watchedService ?? item?.service;
    const currentStartDate = watchedStartDate ?? item?.start_date;
    const currentEndDate = watchedEndDate ?? item?.end_date;
    const currentServiceId = currentService?.id || currentService;

    const isServiceSelected = Boolean(currentServiceId);
    const endDateTime = extractTimeFromDate(currentEndDate);
    // const endDateTimeOptions = generateDefaultTimeOptions();
    const endDateTimeOptions = isServiceSelected ? generateDefaultTimeOptions() : generateNoServiceTimeOptions();

    const { selectedServiceMaxDuration, currentTime, isTimeInvalid, timeOptions } = useSessionTimeOptions({
        currentServiceId,
        currentStartDate,
        serviceTimesData,
        servicesItems: servicesData?.items,
    });

    useEffect(() => {
        if (!entityForm) return;
        const startDateParam = searchParams.get("start_date");
        if (!startDateParam) return;
        
        const parsedDate = parseStartDateParam(startDateParam);
        entityForm.setValue("start_date", parsedDate);
    }, [searchParams, entityForm]);

    useEffect(() => {
        if (!entityForm || !currentStartDate) return;

        const { start_date: isStartDateDirty, service: isServiceDirty } = entityForm.formState.dirtyFields;
        if (!isStartDateDirty && !isServiceDirty) return;

        const startDate = toDate(currentStartDate);
        if (!startDate) return;

        if (selectedServiceMaxDuration) {
            const calculatedEndDate = addMinutes(startDate, selectedServiceMaxDuration);
            entityForm.setValue("end_date", calculatedEndDate, { shouldDirty: true });
        } else {
            const existingEndDate = toDate(entityForm.getValues("end_date"));
            if (!existingEndDate) return;
            const syncedEndDate = combineDateAndTime(startDate, existingEndDate.getHours(), existingEndDate.getMinutes());
            entityForm.setValue("end_date", syncedEndDate, { shouldDirty: true });
        }
    }, [currentService, currentStartDate, selectedServiceMaxDuration, entityForm]);

    const handleDateChange = useCallback((newDate: Date | null) => {
        if (!entityForm) return;

        if (!newDate) {
            entityForm.setValue("start_date", null, { shouldDirty: true });
            return;
        }

        const currentValue = entityForm.getValues("start_date");
        const existingDate = toDate(currentValue);
        const existingTime = existingDate
            ? { hours: existingDate.getHours(), minutes: existingDate.getMinutes() }
            : DEFAULT_TIME;

        const combinedDate = combineDateAndTime(newDate, existingTime.hours, existingTime.minutes);
        entityForm.setValue("start_date", combinedDate, { shouldDirty: true });
    }, [entityForm]);

    const handleTimeChange = useCallback((timeString: string) => {
        if (!entityForm) return;

        const { hours, minutes } = parseTimeString(timeString);
        const currentValue = entityForm.getValues("start_date");
        const baseDate = toDate(currentValue) || new Date();

        const combinedDate = combineDateAndTime(baseDate, hours, minutes);
        entityForm.setValue("start_date", combinedDate, { shouldDirty: true });
    }, [entityForm]);

    const handleEndTimeChange = useCallback((timeString: string) => {
        if (!entityForm) return;

        const { hours, minutes } = parseTimeString(timeString);
        const currentEndDate = entityForm.getValues("end_date");
        const currentStartDate = entityForm.getValues("start_date");
        const baseDate = toDate(currentEndDate) || toDate(currentStartDate) || new Date();

        const combinedDate = combineDateAndTime(baseDate, hours, minutes);
        entityForm.setValue("end_date", combinedDate, { shouldDirty: true });
    }, [entityForm]);

    if (!recordContext || !entityForm || !formatedFields) return null;

    const serviceField = formatedFields.find((f) => f.field === "service");
    const serviceExtraOption = sessionsOptions.fieldsWithExtraOptions?.find((opt) => opt.field === "service");
    const filteredFields = formatedFields.filter((f) => !CUSTOM_HANDLED_FIELDS.includes(f.field));

    return (
        <Box sx={{ backgroundColor: "#fff" }}>
            <Box sx={{ px: 4, pt: 4, pb: 4 }}>
                <GroupsTitle title="Agendamento da Sessão" />
            </Box>

            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={pt}>
                <Box sx={{ display: "flex", gap: 2, px: 4, pb: 4, flexWrap: "wrap" }}>
                    {serviceField && (
                        <Box sx={{ minWidth: 200, flex: 1 }}>
                            <RelatedInputField
                                field={serviceField}
                                value={item?.service || null}
                                fieldView="group"
                                entityForm={entityForm}
                                options={{ variant: "outlined" }}
                                extraOptions={serviceExtraOption}
                            />
                        </Box>
                    )}

                    <Box sx={{ minWidth: 180, flex: 1 }}>
                        <Controller
                            name="start_date"
                            control={entityForm.control}
                            defaultValue={toDate(item?.start_date)}
                            render={({ field }) => (
                                <DatePicker
                                    label="Data"
                                    format="dd/MM/yyyy"
                                    value={toDate(field.value)}
                                    onChange={handleDateChange}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            variant: "outlined",
                                        },
                                    }}
                                />
                            )}
                        />
                    </Box>

                    <Box sx={{ minWidth: 120, flex: 0.5 }}>
                        <FormControl fullWidth variant="outlined" error={isTimeInvalid}>
                            <InputLabel>Hora Início</InputLabel>
                            <Select
                                value={currentTime}
                                label="Hora Início"
                                onChange={(e) => handleTimeChange(e.target.value)}
                            >
                                {timeOptions.map((time) => (
                                    <MenuItem
                                        key={time}
                                        value={time}
                                        sx={isTimeInvalid && time === currentTime ? { color: "error.main" } : {}}
                                    >
                                        {time}
                                    </MenuItem>
                                ))}
                            </Select>
                            {isTimeInvalid && (
                                <Box component="span" sx={{ color: "error.main", fontSize: "0.75rem", mt: 0.5 }}>
                                    Hora inválida
                                </Box>
                            )}
                        </FormControl>
                    </Box>

                    <Box sx={{ minWidth: 120, flex: 0.5 }}>
                        <FormControl fullWidth variant="outlined" disabled={isServiceSelected}>
                            <InputLabel>Hora Fim</InputLabel>
                            <Select
                                value={endDateTime}
                                label="Hora Fim"
                                onChange={(e) => handleEndTimeChange(e.target.value)}
                            >
                                {endDateTimeOptions.map((time) => (
                                    <MenuItem key={time} value={time}>
                                        {time}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    <Box sx={{ minWidth: 180, flex: 1 }}>
                        <Controller
                            name="end_date"
                            control={entityForm.control}
                            defaultValue={toDate(item?.end_date)}
                            render={({ field }) => (
                                <TextField
                                    label="Data de Fim"
                                    fullWidth
                                    variant="outlined"
                                    disabled
                                    value={formatDateTimeDisplay(field.value)}
                                    InputProps={{ readOnly: true }}
                                />
                            )}
                        />
                    </Box>
                </Box>
            </LocalizationProvider>

            <Groups
                item={item}
                fields={filteredFields}
                entityForm={entityForm}
                type={recordContext.type}
                groupsOptions={sessionsOptions}
            />
        </Box>
    );
};

